import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { Periodo } from '@/lib/periodo-reporte'

/** Fila de reporte_financiero_resumen. Las RPC no están en database.ts; se castean. */
export interface ResumenFinanciero {
  ingresos: number
  ingresosAnt: number
  facturado: number
  facturadoAnt: number
  deudaActivos: number
  deudaActivosAnt: number
  deudoresActivos: number
  deudaInactivos: number
  primerPago: string | null // 'YYYY-MM-DD'
}

export type TramoAntiguedad = '0-29' | '30-60' | '61-90' | '90+' | 'sin_imputar'

/** Fila de reporte_antiguedad_deuda. El monto de 'sin_imputar' es negativo. */
export interface AntiguedadTramo {
  tramo: TramoAntiguedad
  monto: number
  deudas: number
  clientes: number
}

/** Fila de reporte_ingresos_por_mes_cuenta. `tipoCuenta` es cuentas.tipo en minúscula. */
export interface IngresoMesCuenta {
  mesAnio: string // 'YYYY-MM'
  tipoCuenta: string
  total: number
  cantidad: number
}

/** Movimiento de gasto de una categoría, para el detalle del Estado de Resultado. */
export interface MovimientoGasto {
  id: string
  fecha: string // 'YYYY-MM-DD'
  descripcion: string
  monto: number
}

type ResumenRow = {
  ingresos: number | string
  ingresos_ant: number | string
  facturado: number | string
  facturado_ant: number | string
  deuda_activos: number | string
  deuda_activos_ant: number | string
  deudores_activos: number | string
  deuda_inactivos: number | string
  primer_pago: string | null
}

type AntiguedadRow = { tramo: TramoAntiguedad; monto: number | string; deudas: number | string; clientes: number | string }

type IngresoRow = { mes_anio: string; tipo_cuenta: string; total: number | string; cantidad: number | string }

const RESUMEN_VACIO: ResumenFinanciero = {
  ingresos: 0,
  ingresosAnt: 0,
  facturado: 0,
  facturadoAnt: 0,
  deudaActivos: 0,
  deudaActivosAnt: 0,
  deudoresActivos: 0,
  deudaInactivos: 0,
  primerPago: null,
}

/**
 * Servicio del reporte financiero. Las agregaciones corren en Postgres.
 * Aislamiento multi-tenant a nivel app: toda RPC recibe organizacion_id.
 * Postgres devuelve numeric como string, así que todo número se normaliza con Number().
 */
export const reporteFinancieroService = {
  async obtenerResumen(
    client: SupabaseClient<Database>,
    organizacionId: string,
    actual: Periodo,
    anterior: Periodo,
  ): Promise<ResumenFinanciero> {
    const { data, error } = await client.rpc('reporte_financiero_resumen' as never, {
      p_organizacion_id: organizacionId,
      p_desde: actual.desde,
      p_hasta: actual.hasta,
      p_desde_ant: anterior.desde,
      p_hasta_ant: anterior.hasta,
    } as never)
    if (error) throw error

    const fila = (Array.isArray(data) ? data[0] : data) as ResumenRow | null
    if (!fila) return RESUMEN_VACIO
    return {
      ingresos: Number(fila.ingresos) || 0,
      ingresosAnt: Number(fila.ingresos_ant) || 0,
      facturado: Number(fila.facturado) || 0,
      facturadoAnt: Number(fila.facturado_ant) || 0,
      deudaActivos: Number(fila.deuda_activos) || 0,
      deudaActivosAnt: Number(fila.deuda_activos_ant) || 0,
      deudoresActivos: Number(fila.deudores_activos) || 0,
      deudaInactivos: Number(fila.deuda_inactivos) || 0,
      primerPago: fila.primer_pago,
    }
  },

  async obtenerAntiguedad(client: SupabaseClient<Database>, organizacionId: string): Promise<AntiguedadTramo[]> {
    const { data, error } = await client.rpc('reporte_antiguedad_deuda' as never, {
      p_organizacion_id: organizacionId,
    } as never)
    if (error) throw error

    return ((data ?? []) as AntiguedadRow[]).map((r) => ({
      tramo: r.tramo,
      monto: Number(r.monto) || 0,
      deudas: Number(r.deudas) || 0,
      clientes: Number(r.clientes) || 0,
    }))
  },

  async obtenerIngresosPorMesCuenta(
    client: SupabaseClient<Database>,
    organizacionId: string,
    periodo: Periodo,
  ): Promise<IngresoMesCuenta[]> {
    const { data, error } = await client.rpc('reporte_ingresos_por_mes_cuenta' as never, {
      p_organizacion_id: organizacionId,
      p_desde: periodo.desde,
      p_hasta: periodo.hasta,
    } as never)
    if (error) throw error

    return ((data ?? []) as IngresoRow[]).map((r) => ({
      mesAnio: r.mes_anio,
      tipoCuenta: r.tipo_cuenta,
      total: Number(r.total) || 0,
      cantidad: Number(r.cantidad) || 0,
    }))
  },
  /** Fecha 'YYYY-MM-DD' del primer movimiento de la organización, o null si no tiene ninguno. */
  async obtenerPrimerMovimiento(client: SupabaseClient<Database>, organizacionId: string): Promise<string | null> {
    const { data, error } = await client
      .from('movimientos')
      .select('fecha')
      .eq('organizacion_id', organizacionId)
      .order('fecha', { ascending: true })
      .limit(1)
      .maybeSingle()
    if (error) throw error
    return data?.fecha ?? null
  },

  /**
   * Gastos en ARS de una categoría entre dos fechas, sin transferencias, del más reciente al más viejo.
   * `categoriaId` null trae los gastos sin categoría. Aplica los mismos filtros que vista_gastos_mensuales.
   */
  async obtenerMovimientosGasto(
    client: SupabaseClient<Database>,
    organizacionId: string,
    categoriaId: string | null,
    periodo: Periodo,
  ): Promise<MovimientoGasto[]> {
    let query = client
      .from('movimientos')
      .select('id, fecha, concepto, descripcion, debe, moneda')
      .eq('organizacion_id', organizacionId)
      .eq('tipo', 'Gasto')
      .not('es_transferencia', 'is', true)
      .gte('fecha', periodo.desde)
      .lte('fecha', periodo.hasta)
      .order('fecha', { ascending: false })
    query = categoriaId ? query.eq('categoria_id', categoriaId) : query.is('categoria_id', null)
    const { data, error } = await query
    if (error) throw error

    return (data ?? [])
      .filter((m) => !m.moneda || m.moneda === 'ARS')
      .map((m) => ({
        id: m.id,
        fecha: m.fecha,
        descripcion: m.descripcion || m.concepto || 'Sin descripción',
        monto: Number(m.debe) || 0,
      }))
  },
}
